import React, { useState } from 'react'
import { View, Alert, TouchableOpacity } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useNavigation } from '@react-navigation/native'
import { useStyleSheet, StyleService } from '@src/style/service'
import { Checkbox, Icon, Text } from '@components/base'
import { NutrisenseLogo } from '@components'
import { useSignIn } from '@src/hooks/useSignIn'
import { SignInButton, SignInButtonVariant } from '@screens/SignIn'
import { useGoBack } from '@utils'
import { useSnack } from '@utils/navigatorContext'
import { useAllowSignInWithApple, useAllowSignInWithGoogle } from '@utils/hooks'
import { useLazyScreenPreload } from '@src/navigation/utils'

export const CreateAccountOptionsStelo = () => {
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation()
  const showSnack = useSnack()
  const allowSignInWithGoogle = useAllowSignInWithGoogle()
  const allowSignInWithApple = useAllowSignInWithApple()
  const { signUpWithApple, signUpWithGoogle } = useSignIn()
  const [termsAccepted, setTermsAccepted] = useState(false)

  useLazyScreenPreload('OnboardingNavigator')

  const confirmTermsOfService = () => {
    return new Promise<boolean>((resolve) => {
      Alert.alert(
        'Terms of Service and Policies',
        `I confirm that I acknowledge and agree to Nutrisense's Terms of Service, Telehealth Consent, Privacy Policy, Medical Disclaimer, HIPAA Authorization, Consumer Health Data Privacy Policy and Courtesy Reminder`,
        [
          { text: 'Cancel', onPress: () => resolve(false) },
          { text: 'Agree', onPress: () => resolve(true), style: 'cancel' },
        ],
        { cancelable: true },
      )
    })
  }

  const handleSignUp = async (callback: () => Promise<void> | void) => {
    if (!termsAccepted) {
      const confirmed = await confirmTermsOfService()

      if (!confirmed) {
        return
      }

      setTermsAccepted(true)
    }

    try {
      await callback()
    } catch (error: any) {
      showSnack(error.message, null, 'error')
    }
  }

  const onSignUpWithEmailPress = () => {
    handleSignUp(() => {
      navigation.navigate('CreateAccountStelo')
    })
  }

  const onSignUpWithGooglePress = () => {
    handleSignUp(signUpWithGoogle)
  }

  const onSignUpWithApplePress = () => {
    handleSignUp(signUpWithApple)
  }

  const goBack = useGoBack()

  const insets = useSafeAreaInsets()

  useLazyScreenPreload('Legal')

  const onLegalPress = () => {
    navigation.navigate('Legal')
  }

  return (
    <View
      style={[styles.container, { paddingTop: insets.top + 12, paddingBottom: insets.bottom + 16 }]}
    >
      <TouchableOpacity
        onPress={goBack}
        activeOpacity={0.7}
        accessibilityLabel="Go Back"
        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
      >
        <Icon name="caret-left" size="20" weight="bold" />
      </TouchableOpacity>
      <View style={styles.headerContainer} testID="SigninScreen/Logo">
        <NutrisenseLogo style={styles.nutrisenseLogo} />
        <Text type="title-3">Create your account</Text>
      </View>
      <View style={styles.mainContent}>
        <SignInButton
          variant={SignInButtonVariant.Email}
          style={styles.signInButton}
          onPress={onSignUpWithEmailPress}
        />
        {allowSignInWithGoogle && (
          <SignInButton
            variant={SignInButtonVariant.Google}
            style={styles.signInButton}
            onPress={onSignUpWithGooglePress}
          >
            Sign Up with Google
          </SignInButton>
        )}
        {allowSignInWithApple && (
          <SignInButton
            variant={SignInButtonVariant.Apple}
            style={styles.signInButton}
            onPress={onSignUpWithApplePress}
          >
            Sign Up with Apple
          </SignInButton>
        )}
      </View>
      <View style={styles.bottom}>
        <Checkbox
          style={styles.checkbox}
          checked={termsAccepted}
          testID="SignInOptionsScreen/TermsOfService"
          onChange={() => setTermsAccepted((prev) => !prev)}
          text={
            <View style={styles.termsOfServiceContainer}>
              <Text type="regular">
                By getting started, you agree to our{' '}
                <Text type="regular" bold style={styles.highlight}>
                  Terms of Service
                </Text>
                ,{' '}
                <Text type="regular" bold style={styles.highlight}>
                  Telehealth Consent
                </Text>
                ,{' '}
                <Text type="regular" bold style={styles.highlight}>
                  Privacy Policy
                </Text>
                ,{' '}
                <Text type="regular" bold style={styles.highlight}>
                  Medical Disclaimer
                </Text>
                ,{' '}
                <Text type="regular" bold style={styles.highlight}>
                  HIPAA Authorization
                </Text>
                ,{' '}
                <Text type="regular" bold style={styles.highlight}>
                  Consumer Health Data Privacy Policy
                </Text>{' '}
                and{' '}
                <Text type="regular" bold style={styles.highlight}>
                  Courtesy Reminder
                </Text>
              </Text>
              <TouchableOpacity
                onPress={onLegalPress}
                accessibilityLabel="Learn More"
                style={styles.learnMoreContainer}
                hitSlop={{ top: 16, right: 16, left: 16, bottom: 16 }}
              >
                <Text type="regular" bold style={styles.link}>
                  Learn More
                </Text>
              </TouchableOpacity>
            </View>
          }
        />
      </View>
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
    paddingHorizontal: 16,
  },
  headerContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nutrisenseLogo: {
    marginBottom: 16,
  },
  mainContent: {
    borderRadius: 24,
    backgroundColor: 'theme.surface.base2',
    paddingHorizontal: 16,
    paddingVertical: 24,
    gap: 16,
  },
  signInButton: {
    backgroundColor: 'theme.background',
  },
  bottom: {
    flex: 1,
    justifyContent: 'flex-end',
    marginTop: 24,
    marginHorizontal: 8,
  },
  checkbox: {
    alignItems: 'flex-start',
  },
  termsOfServiceContainer: {
    paddingLeft: 8,
    flex: 1,
  },
  learnMoreContainer: {
    alignSelf: 'flex-start',
  },
  highlight: {
    color: 'theme.text.secondary',
  },
  link: {
    color: 'theme.text.link',
  },
})
